import { useState } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import StarRating from '@/components/ui/StarRating';
import WineStyleBar from '@/components/ui/WineStyleBar';
import type { ScannedWine } from '@/hooks/useWineScanner';

interface WineScanResultProps {
  wine: ScannedWine;
  onSave: (wine: ScannedWine, rating: number, notes: string, addToCellar: boolean, tastedAt: string) => Promise<void>;
  onRetry: () => void;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function WineScanResult({ wine, onSave, onRetry }: WineScanResultProps) {
  const [edited, setEdited] = useState<ScannedWine>(wine);
  const [rating, setRating] = useState(wine.existingRating ?? 0);
  const [notes, setNotes] = useState(wine.existingNotes ?? '');
  const [tastedAt, setTastedAt] = useState(
    wine.existingTastedAt ? wine.existingTastedAt.slice(0, 10) : todayISO()
  );
  const [addToCellar, setAddToCellar] = useState(false);
  const [saving, setSaving] = useState(false);

  const isUpdate = !!wine.existingUserWineId;

  function update(field: keyof ScannedWine, value: string | number | null) {
    setEdited((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    setSaving(true);
    await onSave(edited, rating, notes, addToCellar, new Date(tastedAt).toISOString());
    setSaving(false);
  }

  return (
    <div className="scan-result">
      <div className="scan-result-header">
        <h2 className="scan-result-name">{edited.name ?? 'Unknown wine'}</h2>
        <p className="scan-result-sub">
          {[edited.producer, edited.vintage, edited.region].filter(Boolean).join(' · ')}
        </p>
        {isUpdate && (
          <p className="scan-result-update-notice">You've logged this wine before — updating your previous entry.</p>
        )}
        {edited.confidence === 'low' && !isUpdate && (
          <p className="scan-result-warning">Low confidence scan — please verify the details below.</p>
        )}
      </div>

      <section className="scan-result-fields">
        <h3>Wine details</h3>
        <Input label="Name" value={edited.name ?? ''} onChange={(e) => update('name', e.target.value)} />
        <Input label="Producer" value={edited.producer ?? ''} onChange={(e) => update('producer', e.target.value)} />
        <Input label="Vintage" type="number" value={edited.vintage ?? ''} onChange={(e) => update('vintage', parseInt(e.target.value) || null)} />
        <Input label="Region" value={edited.region ?? ''} onChange={(e) => update('region', e.target.value)} />
        <Input label="Country" value={edited.country ?? ''} onChange={(e) => update('country', e.target.value)} />
      </section>

      {(edited.body || edited.tannin || edited.acidity) && (
        <section className="scan-result-style">
          <h3>Style</h3>
          {edited.body     && <WineStyleBar label="Body"      value={edited.body}      lowLabel="Light"   highLabel="Full"   />}
          {edited.tannin   && <WineStyleBar label="Tannin"    value={edited.tannin}    lowLabel="Soft"    highLabel="Grippy" />}
          {edited.acidity  && <WineStyleBar label="Acidity"   value={edited.acidity}   lowLabel="Low"     highLabel="Crisp"  />}
          {edited.oak      && <WineStyleBar label="Oak"       value={edited.oak}       lowLabel="Unoaked" highLabel="Oaked"  />}
          {edited.sweetness && <WineStyleBar label="Sweetness" value={edited.sweetness} lowLabel="Dry"     highLabel="Sweet"  />}
        </section>
      )}

      {edited.description && (
        <section className="scan-result-ai-notes">
          <h3>About this wine</h3>
          <p className="scan-result-ai-notes-text">{edited.description}</p>
        </section>
      )}

      {edited.aiTastingNotes && (
        <section className="scan-result-ai-notes">
          <h3>AI Tasting Notes</h3>
          <p className="scan-result-ai-notes-text">{edited.aiTastingNotes}</p>
        </section>
      )}

      {(edited.servingTemp || edited.agingPotential || edited.priceRange || edited.foodPairings?.length) && (
        <section className="scan-result-ai-notes">
          <h3>Wine info</h3>
          <dl className="scan-result-info-list">
            {edited.servingTemp && (
              <>
                <dt>Serving temp</dt>
                <dd>{edited.servingTemp}</dd>
              </>
            )}
            {edited.agingPotential && (
              <>
                <dt>Drinking window</dt>
                <dd>{edited.agingPotential}</dd>
              </>
            )}
            {edited.priceRange && (
              <>
                <dt>Price range</dt>
                <dd style={{ textTransform: 'capitalize' }}>{edited.priceRange}</dd>
              </>
            )}
          </dl>
          {!!edited.foodPairings?.length && (
            <p className="scan-result-food-pairings">
              <strong>Pairs with:</strong> {edited.foodPairings.join(', ')}
            </p>
          )}
        </section>
      )}

      <section className="scan-result-rating">
        <h3>Your rating</h3>
        <StarRating value={rating} onChange={setRating} size="lg" />
      </section>

      <section className="scan-result-notes">
        <Input
          label="My Tasting Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Great with the lamb…"
        />
      </section>

      <section className="scan-result-date">
        <Input
          label="Tasting date"
          type="date"
          value={tastedAt}
          onChange={(e) => setTastedAt(e.target.value)}
        />
      </section>

      <label className="scan-result-cellar-toggle">
        <input
          type="checkbox"
          checked={addToCellar}
          onChange={(e) => setAddToCellar(e.target.checked)}
        />
        Add to my cellar
      </label>

      <div className="scan-result-actions">
        <Button variant="ghost" onClick={onRetry}>Scan again</Button>
        <Button variant="primary" onClick={handleSave} loading={saving}>
          {isUpdate ? 'Update tasting' : 'Save tasting'}
        </Button>
      </div>
    </div>
  );
}
